import React, { useState, useEffect } from 'react';
import { useAppStore } from '@/store/appStore';
import {
  BuildingOfficeIcon,
  CogIcon,
  DocumentTextIcon,
  PaintBrushIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  LinkIcon,
  ComputerDesktopIcon,
  FolderIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentListIcon,
  DocumentPlusIcon,
  BoltIcon,
  BookOpenIcon,
  TableCellsIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import FirmInformationPanel from '../components/FirmInformationPanel';
import { EngagementConfigurationRootPanel } from '../components/settings/matter-configuration/components/EngagementConfigurationRootPanel';
import { DocumentManagementPanel } from '../components/settings/document-management/DocumentManagementPanel';
import { SystemPreferencesPanel } from '../components/settings/system-preferences/SystemPreferencesPanel';
import { IntegrationsPanel } from '../components/settings/integrations/IntegrationsPanel';
import MigrationCenterPanel from '../components/settings/migration-center/MigrationCenterPanel';
import TaskTemplatesPanel from '../components/settings/template-management/TaskTemplatesPanel';
import IntakeFormsPanel from '../components/settings/intake-forms/IntakeFormsPanel';
import WorkflowPanel from '../components/settings/workflow-builder/WorkflowPanel';
import PlaybookLibraryPanel from '@/features/playbooks/components/PlaybookLibraryPanel';
import SchemeManagerPanel from '@/features/playbooks/components/SchemeManagerPanel';
import AIModelGovernancePanel from '../components/settings/ai-governance/AIModelGovernancePanel';

// Types for navigation
interface SettingsSection {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  implemented: boolean;
  comingSoon?: string;
  description: string;
}

// Settings navigation structure
const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    id: 'firm-information',
    label: 'Subscriber Profile',
    icon: BuildingOfficeIcon,
    implemented: true,
    description: 'Business identity, service areas, and operating contexts'
  },
  {
    id: 'engagement-configuration',
    label: 'Engagement Configuration',
    icon: DocumentTextIcon,
    implemented: true,
    description: 'Engagement numbering, reusable templates, and default organization settings'
  },
  {
    id: 'migration-center',
    label: 'Migration Center',
    icon: ArrowUpTrayIcon,
    implemented: true,
    description: 'Bulk import engagements, documents, and mappings from legacy systems'
  },
  {
    id: 'document-management',
    label: 'Document Management',
    icon: FolderIcon,
    implemented: true,
    description: 'Document templates, dashboard, and archived document management'
  },
  {
    id: 'task-templates',
    label: 'Capability Templates',
    icon: ClipboardDocumentListIcon,
    implemented: true,
    description: 'Reusable task and workflow templates for enabled service areas'
  },
  {
    id: 'playbook-library',
    label: 'Playbook Library',
    icon: BookOpenIcon,
    implemented: true,
    description: 'Browse, clone, and manage workflow playbooks with versioning, analytics, and validation'
  },
  {
    id: 'playbook-schemes',
    label: 'Workflow Assignments',
    icon: TableCellsIcon,
    implemented: true,
    description: 'Control which playbook runs automatically for each type of work'
  },
  {
    id: 'intake-forms',
    label: 'Intake Forms',
    icon: DocumentPlusIcon,
    implemented: true,
    description: 'Build and manage client intake forms for your website'
  },
  {
    id: 'intake-workflows',
    label: 'Intake Workflows',
    icon: BoltIcon,
    implemented: true,
    description: 'Automate lead processing with trigger-action workflows'
  },
  {
    id: 'branding-templates',
    label: 'Branding & Templates',
    icon: PaintBrushIcon,
    implemented: true,
    description: 'Brand logo, letterhead, and communication templates'
  },
  {
    id: 'security',
    label: 'Security Settings',
    icon: ShieldCheckIcon,
    implemented: false,
    comingSoon: 'Q2 2025',
    description: 'Two-factor authentication, password policies, and access controls'
  },
  {
    id: 'billing',
    label: 'Billing Settings', 
    icon: CreditCardIcon,
    implemented: false,
    comingSoon: 'Q2 2025',
    description: 'Default rates, invoice themes, and payment configuration'
  },
  {
    id: 'integrations',
    label: 'Integrations',
    icon: LinkIcon,
    implemented: true,
    description: 'Storage providers, API keys, industry systems, and third-party connections'
  },
  {
    id: 'ai-model-governance',
    label: 'AI Model Governance',
    icon: CpuChipIcon,
    implemented: true,
    description: 'Control which AI models this subscriber can use, set defaults, and monitor usage'
  },
  {
    id: 'system-preferences',
    label: 'System Preferences',
    icon: ComputerDesktopIcon,
    implemented: true,
    description: 'Operating contexts, service areas, time zones, and defaults'
  }
];

/**
 * Settings Navigation Sidebar Component
 */
interface SettingsNavigationProps {
  activeSection: string;
  onSectionChange: (sectionId: string) => void;
}

const SettingsNavigation: React.FC<SettingsNavigationProps> = ({ 
  activeSection, 
  onSectionChange 
}) => {
  return (
    <div className="w-full lg:w-80 bg-gray-50 dark:bg-gray-800 lg:border-r border-gray-200 dark:border-gray-700 lg:min-h-full">
      <div className="p-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
          Settings Categories
        </h2>
        <nav className="space-y-1 lg:space-y-2">
          {SETTINGS_SECTIONS.map((section) => {
            const isActive = activeSection === section.id;
            const Icon = section.icon;
            
            return (
              <button
                key={section.id}
                onClick={() => onSectionChange(section.id)}
                className={`w-full flex items-start space-x-3 px-3 py-2 lg:py-3 rounded-lg text-left transition-all duration-200 group ${
                  isActive
                    ? 'bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent border border-lex-accent dark:border-lex-accent'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white'
                } ${!section.implemented ? 'opacity-75' : ''}`}
                disabled={!section.implemented}
              >
                <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                  isActive 
                    ? 'text-lex-accent dark:text-lex-accent'
                    : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-medium truncate">
                      {section.label}
                    </span>
                    {!section.implemented && section.comingSoon && (
                      <span className="text-xs bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                        Soon
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                    {section.description}
                  </p>
                  {!section.implemented && section.comingSoon && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                      Expected: {section.comingSoon}
                    </p>
                  )}
                </div>
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

/**
 * Placeholder Content Component
 */
interface PlaceholderContentProps {
  section: SettingsSection;
}

const PlaceholderContent: React.FC<PlaceholderContentProps> = ({ section }) => {
  const Icon = section.icon;
  
  if (!section.implemented) {
    return (
      <div className="flex items-center justify-center min-h-[300px] lg:min-h-[500px] px-4">
        <div className="text-center max-w-md w-full">
          <div className="mx-auto flex items-center justify-center h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-amber-100 dark:bg-amber-900/20 mb-3 lg:mb-4">
            <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-amber-600 dark:text-amber-400" />
          </div>
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {section.label}
          </h3>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 mb-3 lg:mb-4">
            {section.description}
          </p>
          <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-3 lg:p-4">
            <div className="flex items-center justify-center space-x-2 text-amber-700 dark:text-amber-300">
              <CogIcon className="h-4 w-4" />
              <span className="text-xs lg:text-sm font-medium">Coming Soon - {section.comingSoon}</span>
            </div>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
              This feature is currently under development and will be available in a future release.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-[300px] lg:min-h-[500px] px-4">
      <div className="text-center max-w-md w-full">
        <div className="mx-auto flex items-center justify-center h-10 w-10 lg:h-12 lg:w-12 rounded-full bg-lex-accent-soft dark:bg-lex-accent-soft mb-3 lg:mb-4">
          <Icon className="h-5 w-5 lg:h-6 lg:w-6 text-lex-accent dark:text-lex-accent" />
        </div>
        <h3 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-2">
          {section.label}
        </h3>
        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400 mb-3 lg:mb-4">
          {section.description}
        </p>
        <div className="bg-lex-accent-soft dark:bg-lex-accent-soft border border-lex-accent dark:border-lex-accent rounded-lg p-3 lg:p-4">
          <div className="flex items-center justify-center space-x-2 text-lex-accent dark:text-lex-accent">
            <CogIcon className="h-4 w-4" />
            <span className="text-xs lg:text-sm font-medium">Ready for Implementation</span>
          </div>
          <p className="text-xs text-lex-accent dark:text-lex-accent mt-2">
            This section will be implemented in the next development checkpoint.
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * Main Content Area Component
 */
interface SettingsContentProps {
  activeSection: string;
}

const SettingsContent: React.FC<SettingsContentProps> = ({ activeSection }) => {
  const section = SETTINGS_SECTIONS.find(s => s.id === activeSection);
  
  if (!section) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Section Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The requested settings section could not be found.
          </p>
        </div>
      </div>
    );
  }

  // Render actual implementation for firm-information section
  if (activeSection === 'firm-information') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <div className="mb-4 lg:mb-6">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-2">
              {section.label}
            </h2>
            <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
              {section.description}
            </p>
          </div>
          
          <FirmInformationPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'engagement-configuration') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <EngagementConfigurationRootPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'migration-center') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <MigrationCenterPanel />
        </div>
      </div>
    );
  }

  // Render actual implementation for document-management section
  if (activeSection === 'document-management') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <DocumentManagementPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'task-templates') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <TaskTemplatesPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'playbook-library') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <PlaybookLibraryPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'playbook-schemes') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <SchemeManagerPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'intake-forms') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <IntakeFormsPanel />
        </div>
      </div>
    );
  }

  if (activeSection === 'intake-workflows') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <WorkflowPanel />
        </div>
      </div>
    );
  }

  // Render actual implementation for system-preferences section
  if (activeSection === 'system-preferences') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <SystemPreferencesPanel />
        </div>
      </div>
    );
  }

  // Render AI Model Governance section
  if (activeSection === 'ai-model-governance') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <AIModelGovernancePanel />
        </div>
      </div>
    );
  }

  // Render actual implementation for integrations section
  if (activeSection === 'integrations') {
    return (
      <div className="flex-1 bg-white dark:bg-gray-900">
        <div className="p-4 lg:p-6">
          <IntegrationsPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white dark:bg-gray-900">
      <div className="p-4 lg:p-6">
        <div className="mb-4 lg:mb-6">
          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-white mb-2">
            {section.label}
          </h2>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            {section.description}
          </p>
        </div>
        
        <PlaceholderContent section={section} />
      </div>
    </div>
  );
};

/**
 * Main Firm Settings Application Component
 */
const FirmSettings: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('firm-information');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  // Collapse the EdgeLex rail when Firm Settings opens
  const setSidebarExpanded = useAppStore((s) => s.setSidebarExpanded);
  useEffect(() => { setSidebarExpanded(false); }, [setSidebarExpanded]);

  // Handle mobile navigation
  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false); // Close mobile menu after selection
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile navigation toggle button */}
      <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <CogIcon className="h-5 w-5" />
          <span className="font-medium">Settings Menu</span>
          <svg 
            className={`h-4 w-4 transition-transform ${isMobileMenuOpen ? 'rotate-180' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      <div className="flex flex-col lg:flex-row">
        {/* Desktop sidebar - always visible on lg+ */}
        <div className="hidden lg:block">
          <SettingsNavigation 
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        </div>

        {/* Mobile sidebar - toggleable */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <SettingsNavigation 
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
            />
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 min-w-0">
          <SettingsContent activeSection={activeSection} />
        </div>
      </div>
    </div>
  );
};

export default FirmSettings;
